import { Button, Paper, Stack, Tooltip } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { SearchableSelect } from '../SearchableSelect';
import {
  DEFAULT_TIME_FILTER,
  TimeFilter,
  type TimePresetKeyWithField
} from '../TimeFilter';
import QueryBuilder from './QueryBuilder';
import { DEFAULT_QB_FILTER_KEY, QBOperator } from './QueryBuilder.constants';
import { useQBFilterActions } from './QueryBuilder.hooks';
import useStyles from './QueryBuilder.styles';
import type {
  QBBarProps,
  QBFilter,
  QBFiltersConfig
} from './QueryBuilder.types';
import { getFieldOptions, getSelectedOptions } from './QueryBuilder.utils';

export const QueryBuilderBar = function QueryBuilderBar({
  config,
  enableTimeFilter = false,
  createdTimeLabel,
  lastUpdatedTimeLabel,
  options,
  disableTimeFilterType,
  onChange,
  onTimeFilterChange,
  sessionKey,
  searchableSelectFilters = [],
  filters,
  timeFilter
}: QBBarProps) {
  const { classes } = useStyles();

  const { updateFilters, removeFilter, clearFilters, setTimeFilter } =
    useQBFilterActions(sessionKey || DEFAULT_QB_FILTER_KEY);

  // This merges the base config with any options provided for the searchable select filters,
  // this is necessary to ensure that the QueryBuilder has the correct options for each filter when rendering the filter dropdowns
  const filtersConfig = useMemo(() => {
    const result = {} as QBFiltersConfig;
    Object.entries(config).forEach(([baseField, fieldConfig]) => {
      result[baseField] = {
        ...fieldConfig,
        ...(options?.[baseField] ? { options: options[baseField] } : {})
      };
    });
    return result;
  }, [config, options]);

  // This is used to manage the selected options for the searchable select filters
  const selectedOptions = useMemo(() => {
    const result: Record<string, string[]> = {};
    filters?.forEach((filter) => {
      // Map null-check operators to the "Null"/"NotNull" quick filter options for any field that defines them
      const hasNullOptions = searchableSelectFilters.some(
        (s) =>
          s.field === filter.field &&
          s.filterOptions?.some((opt) =>
            ['Null', 'NotNull'].includes(String(opt.value))
          )
      );
      if (hasNullOptions) {
        const isNull =
          filter.operator === QBOperator.IsNull && filter.value !== 'false';
        const isNotNull =
          filter.operator === QBOperator.IsNotNull ||
          (filter.operator === QBOperator.IsNull && filter.value === 'false');
        if (isNull || isNotNull) {
          result[filter.field] = [isNull ? 'Null' : 'NotNull'];
          return;
        }
      }

      const fieldOptions = getFieldOptions(filtersConfig, filter.field);
      result[filter.field] = getSelectedOptions(filter, fieldOptions).map(
        (opt) => String(opt.value)
      );
    });
    return result;
  }, [filters, filtersConfig, searchableSelectFilters]);

  const handleOnChange = useCallback(
    (filter: QBFilter) => {
      updateFilters([filter]);
      onChange?.([filter]);
    },
    [updateFilters, onChange]
  );

  const handleOnDelete = useCallback(
    (field: string) => {
      removeFilter(field);
    },
    [removeFilter]
  );

  const handleQuickFilterApply = (
    key: string,
    filterOptions: string[]
  ): void => {
    if (config[key]) {
      // clear options, e.g. when toggle button is turned off
      if (filterOptions.length === 0) {
        removeFilter(key);
        onChange?.([]);
        return;
      }

      // if only one option is selected, use __eq operator
      if (filterOptions.length === 1) {
        const filter: QBFilter = {
          field: key,
          operator: QBOperator.Eq,
          value: filterOptions[0]
        };

        if (filterOptions[0] === 'Null') {
          filter.operator = QBOperator.IsNull;
          filter.value = 'true';
        }

        if (filterOptions[0] === 'NotNull') {
          filter.operator = QBOperator.IsNotNull;
          filter.value = 'false';
        }
        updateFilters([filter]);
        onChange?.([filter]);
      }

      // if multiple options are selected, use __in operator if it's available in the config
      if (
        filterOptions.length > 1 &&
        config[key].operators.includes(QBOperator.In)
      ) {
        const filter: QBFilter = {
          field: key,
          operator: QBOperator.In,
          value: filterOptions.filter((opt) => opt !== 'Null').join(',')
        };
        updateFilters([filter]);
        onChange?.([filter]);
      }
    } else {
      console.warn(`No filter configuration found for key: ${key}`);
    }
  };

  const handleTimeFilterChange = useCallback(
    (time: TimePresetKeyWithField) => {
      setTimeFilter(time);
      onTimeFilterChange?.(time);
    },
    [setTimeFilter, onTimeFilterChange]
  );

  return (
    <Stack
      direction="row"
      sx={{ flexWrap: 'wrap', gap: 1 }}
      className={classes.queryBuilderBarContainer}
    >
      <Stack direction="row" sx={{ alignItems: 'center', flex: 1 }}>
        {enableTimeFilter && (
          <TimeFilter
            selectedTime={
              (timeFilter as TimePresetKeyWithField) || DEFAULT_TIME_FILTER
            }
            onChange={handleTimeFilterChange}
            disableTimeFilterType={disableTimeFilterType}
            createdTimeLabel={createdTimeLabel}
            lastUpdatedTimeLabel={lastUpdatedTimeLabel}
          />
        )}
        <Stack
          direction="row"
          sx={{ flexGrow: 1 }}
          spacing={1}
          className={
            classes.queryBuilderBar +
            (enableTimeFilter ? ' withTimeFilter' : '')
          }
        >
          {filters?.map((filter) => (
            <QueryBuilder
              key={filter.field + filter.operator + filter.value}
              config={filtersConfig}
              filter={filter}
              onChange={handleOnChange}
              onDelete={handleOnDelete}
            />
          ))}
          <QueryBuilder
            config={filtersConfig}
            onChange={handleOnChange}
            onDelete={handleOnDelete}
          />
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              flexGrow: 1,
              justifyContent: 'flex-end'
            }}
            spacing={1}
          >
            {filters && filters.length > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={clearFilters}
                sx={{ px: 1, py: 0.25 }}
              >
                Clear
              </Button>
            )}
            <Tooltip
              title="Use keyboard shortcut '/' to start searching"
              placement="top"
            >
              <Paper
                variant="outlined"
                className={classes.queryBuilderShortcut}
              >
                /
              </Paper>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
      {searchableSelectFilters
        ?.filter((f) => !f.hidden)
        .map((filter) => (
          <SearchableSelect
            key={filter.field}
            field={filter.field}
            filterOptions={filter.filterOptions}
            title={filter.title}
            variant={filter.config.variant!}
            searchable={filter.config.searchable}
            showCounts={filter.config.showCounts}
            selectedOptions={selectedOptions[filter.field] || []}
            onApply={handleQuickFilterApply}
          />
        ))}
    </Stack>
  );
};

export default QueryBuilderBar;
